import React, { useEffect, useState, useCallback } from 'react';
import { getEvents } from '../services/api';
import './Events.css';

function formatDate(iso, opts) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', opts || {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const PAGE_SIZE = 9;

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);

  useEffect(() => {
    getEvents()
      .then(setEvents)
      .catch(() => setError('Impossible de charger les événements.'))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setSelected(null), []);

  // Close modal on Escape key
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, close]);

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.date) >= now);
  const past = events.filter((e) => new Date(e.date) < now);

  const upcomingTotal = Math.ceil(upcoming.length / PAGE_SIZE);
  const pastTotal = Math.ceil(past.length / PAGE_SIZE);
  const upcomingSlice = upcoming.slice((upcomingPage - 1) * PAGE_SIZE, upcomingPage * PAGE_SIZE);
  const pastSlice = past.slice((pastPage - 1) * PAGE_SIZE, pastPage * PAGE_SIZE);

  return (
    <div className="events-page">
      <div className="page-banner">
        <div className="container">
          <h1>Événements</h1>
          <p>Restez informés des activités de notre communauté</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading && (
            <div className="spinner-wrap"><div className="spinner" /></div>
          )}
          {error && (
            <div className="state-box"><h3>Erreur</h3><p>{error}</p></div>
          )}

          {!loading && !error && (
            <>
              <h2 className="section-title">Événements à Venir</h2>
              <div className="gold-bar" />
              {upcoming.length === 0 ? (
                <div className="state-box">
                  <h3>Aucun événement à venir</h3>
                  <p>Revenez bientôt pour voir les prochaines activités!</p>
                </div>
              ) : (
                <>
                  <div className="events-full-grid">
                    {upcomingSlice.map((ev) => (
                      <EventCard key={ev.id} event={ev} onClick={() => setSelected(ev)} />
                    ))}
                  </div>
                  {upcomingTotal > 1 && (
                    <Pagination current={upcomingPage} total={upcomingTotal} onChange={setUpcomingPage} />
                  )}
                </>
              )}

              {past.length > 0 && (
                <div style={{ marginTop: '64px' }}>
                  <h2 className="section-title" style={{ color: 'var(--text-muted)' }}>
                    Événements Passés
                  </h2>
                  <div className="gold-bar" />
                  <div className="events-full-grid events-full-grid--past">
                    {pastSlice.map((ev) => (
                      <EventCard key={ev.id} event={ev} past onClick={() => setSelected(ev)} />
                    ))}
                  </div>
                  {pastTotal > 1 && (
                    <Pagination current={pastPage} total={pastTotal} onChange={setPastPage} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {selected && <EventModal event={selected} onClose={close} />}
    </div>
  );
}

function Pagination({ current, total, onChange }) {
  const scrollToSection = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const go = (page) => { onChange(page); scrollToSection(); };

  // Build page number array with ellipsis logic
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  return (
    <div className="pagination">
      <button
        className="pagination__btn"
        disabled={current === 1}
        onClick={() => go(current - 1)}
        aria-label="Page précédente"
      >
        ‹
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} className="pagination__ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`pagination__btn${p === current ? ' pagination__btn--active' : ''}`}
            onClick={() => go(p)}
            aria-current={p === current ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        className="pagination__btn"
        disabled={current === total}
        onClick={() => go(current + 1)}
        aria-label="Page suivante"
      >
        ›
      </button>
    </div>
  );
}

function EventCard({ event, past, onClick }) {
  const d = new Date(event.date);
  return (
    <article
      className={`event-card card${past ? ' event-card--past' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      style={{ cursor: 'pointer' }}
    >
      {event.image_url && (
        <div className="event-card__img-wrap">
          <img src={event.image_url} alt={event.title} className="event-card__img" />
          <div className="event-card__img-overlay" />
          <div className="event-card__date-badge">
            <span className="event-card__day">{d.getDate()}</span>
            <span className="event-card__month">
              {d.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
            </span>
          </div>
        </div>
      )}
      <div className="event-card__body">
        {!event.image_url && (
          <div className="event-card__date-col">
            <span className="event-card__day">{d.getDate()}</span>
            <span className="event-card__month">
              {d.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
            </span>
            <span className="event-card__year">{d.getFullYear()}</span>
          </div>
        )}
        <div className="event-card__content">
          <div className="event-card__tags">
            {event.is_recurring && <span className="badge">Récurrent</span>}
            {past && <span className="badge" style={{ background: 'var(--text-muted)' }}>Passé</span>}
          </div>
          <h3>{event.title}</h3>
          <p className="event-card__time">
            🕐 {formatTime(event.date)}
            {event.end_date && ` — ${formatTime(event.end_date)}`}
          </p>
          {event.location && (
            <p className="event-card__location">📍 {event.location}</p>
          )}
          {event.description && (
            <p className="event-card__desc">{event.description}</p>
          )}
          <span className="event-card__more">Voir les détails ›</span>
        </div>
      </div>
    </article>
  );
}

function EventModal({ event, onClose }) {
  const d = new Date(event.date);
  const fullDate = d.toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="ev-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ev-modal__close" onClick={onClose} aria-label="Fermer">&times;</button>

        {event.image_url && (
          <div className="ev-modal__img-wrap">
            <img src={event.image_url} alt={event.title} className="ev-modal__img" />
            <div className="ev-modal__img-overlay" />
            <div className="ev-modal__date-badge">
              <span className="ev-modal__day">{d.getDate()}</span>
              <span className="ev-modal__month">
                {d.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
              </span>
            </div>
          </div>
        )}

        <div className="ev-modal__body">
          <div className="ev-modal__tags">
            {event.is_recurring && <span className="badge">Récurrent</span>}
          </div>
          <h2 className="ev-modal__title">{event.title}</h2>

          <div className="ev-modal__meta">
            <div className="ev-modal__meta-row">
              <span className="ev-modal__meta-icon">📅</span>
              <span>{fullDate}</span>
            </div>
            <div className="ev-modal__meta-row">
              <span className="ev-modal__meta-icon">🕐</span>
              <span>
                {formatTime(event.date)}
                {event.end_date && ` — ${formatTime(event.end_date)}`}
              </span>
            </div>
            {event.location && (
              <div className="ev-modal__meta-row">
                <span className="ev-modal__meta-icon">📍</span>
                <span>{event.location}</span>
              </div>
            )}
          </div>

          {event.description && (
            <p className="ev-modal__desc">{event.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
